/**
 * @module actions/Application
 */
import { createAction } from 'typesafe-actions';

import { AppActions } from '../constants/App';

type ApplicationInitializeParams = $TSFixMe;

interface ApplicationInitializePayload {
  store: ApplicationInitializeParams;
}

/**
 * Application init action
 *
 * @param {object} store - redux store
 * @returns {object} application init
 */
export const initApp = createAction(
  AppActions.APPLICATION_INITIALIZE,
  (store: ApplicationInitializeParams) => ({ store }),
)<ApplicationInitializePayload>();
export type ApplicationInitializeAction = ReturnType<typeof initApp>;

interface InitializeClientPayload {
  browserSupportLevel: $TSFixMe;
  isElectron: $TSFixMe;
  ua: $TSFixMe;
}

export const initializeClient = createAction(
  AppActions.INITIALIZE_CLIENT,
  (ua: $TSFixMe, browserSupportLevel: $TSFixMe, isElectron: $TSFixMe) => ({
    ua,
    browserSupportLevel,
    isElectron,
  }),
)<InitializeClientPayload>();
export type InitializeClientAction = ReturnType<typeof initializeClient>;

interface ApplicationBackgroundUpdateParams {
  bgTheme: string;
  navTheme: string;
}

type ApplicationBackgroundUpdatePayload = ApplicationBackgroundUpdateParams;

/**
 * Updates App and Nav background settings
 *
 * @param {object} settings - bg and nav theme names
 * @param {string} settings.bgTheme - background treatment for the full app
 * @param {string} settings.navTheme - background and text color theming for the nav
 */
export const updateAppBackground = createAction(
  AppActions.APPLICATION_BACKGROUND_UPDATE,
  (settings: ApplicationBackgroundUpdateParams) => settings,
)<ApplicationBackgroundUpdatePayload>();
export type ApplicationBackgroundUpdateAction = ReturnType<typeof updateAppBackground>;

type UpdateDominantColorParams = $TSFixMe;

interface UpdateDominantColorPayload {
  color: UpdateDominantColorParams;
}

export const updateDominantColor = createAction(
  AppActions.UPDATE_DOMINANT_COLOR,
  (color: UpdateDominantColorParams) => ({ color }),
)<UpdateDominantColorPayload>();
export type UpdateDominantColorAction = ReturnType<typeof updateDominantColor>;

interface ServiceErrorPayload {
  error: $TSFixMe;
  originalAction: $TSFixMe;
}

/**
 * Action to signal a service error
 *
 * @param {object} error - the error object from the service call
 * @param {object} originalAction - the action that triggered this error
 */
export const serviceError = createAction(
  AppActions.SERVICE_ERROR,
  (error: $TSFixMe, originalAction: $TSFixMe) => ({ error, originalAction }),
)<ServiceErrorPayload>();
export type ServiceErrorAction = ReturnType<typeof serviceError>;

/**
 * Action to signal healthy service response
 */
export const serviceOk = createAction(AppActions.SERVICE_OK)();
export type ServiceOkAction = ReturnType<typeof serviceOk>;

/**
 * Action to signal we are about to poke network to check for healthy service response
 */
export const tryPokingNetwork = createAction(AppActions.TRY_POKING_NETWORK)();
export type TryPokingNetworkAction = ReturnType<typeof tryPokingNetwork>;

interface BufferApiCallsPayload {
  buffer: boolean;
}

/**
 * Action to signal whether API calls need to be buffered
 */
export const bufferApiCalls = createAction(AppActions.BUFFER_API_CALLS, (buffer: boolean) => ({
  buffer,
}))<BufferApiCallsPayload>();
export type BufferApiCallsAction = ReturnType<typeof bufferApiCalls>;

/**
 * Force a re-auth call to the service
 */
export const listenerReauth = createAction(AppActions.LISTENER_REAUTH)();
export type ListenerReauthAction = ReturnType<typeof listenerReauth>;

/**
 * Action to signal that the user is entering a 404 page
 */
export const enter404 = createAction(AppActions.ENTER_404)();
export type Enter404Action = ReturnType<typeof enter404>;

/**
 * Action to signal that the user is leaving a 404 page
 */
export const leave404 = createAction(AppActions.LEAVE_404)();
export type Leave404Action = ReturnType<typeof leave404>;

interface ToggleOfflineModePayload {
  isOffline: boolean;
}

export const switchAppOffline = createAction(AppActions.TOGGLE_OFFLINE_MODE, () => ({
  isOffline: true,
}))<ToggleOfflineModePayload>();

export const switchAppOnline = createAction(AppActions.TOGGLE_OFFLINE_MODE, () => ({
  isOffline: false,
}))<ToggleOfflineModePayload>();

export type ToggleOfflineModeAction = ReturnType<typeof switchAppOffline | typeof switchAppOnline>;

type SetLastOfflineActionParams = $TSFixMe;

interface SetLastOfflineActionPayload {
  lastOfflineAction: SetLastOfflineActionParams;
}

export const setLastOfflineAction = createAction(
  AppActions.SET_LAST_OFFLINE_ACTION,
  (lastOfflineAction: SetLastOfflineActionParams) => ({ lastOfflineAction }),
)<SetLastOfflineActionPayload>();
export type SetLastOfflineActionAction = ReturnType<typeof setLastOfflineAction>;

interface ShowCoachmarkPayload {
  path: string;
}

/**
 * Action to signal that a coachmark must be displayed
 *
 * @param {string} path - the path to be passed to the coachmark
 */
export const showCoachmark = createAction(AppActions.SHOW_COACHMARK, (path: string) => ({
  path,
}))<ShowCoachmarkPayload>();
export type ShowCoachmarkAction = ReturnType<typeof showCoachmark>;

interface ReroutePayload {
  path: string;
}

// Temporary solution to avoid passing around router until things get fully saga'd
export const reroute = createAction(AppActions.REROUTE, (path: string) => ({
  path,
}))<ReroutePayload>();
export type RerouteAction = ReturnType<typeof reroute>;

interface SetPersistingStatePayload {
  isPersisting: boolean;
}

// Set whether we are persisting state locally on store updates
export const setIsPersistingState = createAction(
  AppActions.SET_PERSISTING_STATE,
  (isPersisting: boolean) => ({ isPersisting }),
)<SetPersistingStatePayload>();
export type SetPersistingStateAction = ReturnType<typeof setIsPersistingState>;

// Notify app to free up resources if possible for critical animations
export const notifyCriticalRenderPath = createAction(AppActions.NOTIFY_CRITICAL_RENDER_PATH)();
export type NotifyCriticalRenderPathAction = ReturnType<typeof notifyCriticalRenderPath>;

type SetStartupQueryParametersParams = $TSFixMe;

interface SetStartupQueryParametersPayload {
  startupQueryParameters: SetStartupQueryParametersParams;
}

export const setStartupQueryParameters = createAction(
  AppActions.SET_STARTUP_QUERY_PARAMETERS,
  (startupQueryParameters: SetStartupQueryParametersParams) => ({ startupQueryParameters }),
)<SetStartupQueryParametersPayload>();
export type SetStartupQueryParametersAction = ReturnType<typeof setStartupQueryParameters>;

interface SetIsAdBlockerDetectedPayload {
  loaded: $TSFixMe;
  source: $TSFixMe;
}

export const setIsAdBlockerDetected = createAction(
  AppActions.SET_IS_AD_BLOCKER_DETECTED,
  (loaded: $TSFixMe, source: $TSFixMe) => ({ loaded, source }),
)<SetIsAdBlockerDetectedPayload>();
export type SetIsAdBlockerDetectedAction = ReturnType<typeof setIsAdBlockerDetected>;

type SetIsBrowserAutoplayBlockDetectedParams = $TSFixMe;

interface SetIsBrowserAutoplayBlockDetectedPayload {
  detected: SetIsBrowserAutoplayBlockDetectedParams;
}

export const setIsBrowserAutoplayBlockDetected = createAction(
  AppActions.SET_IS_BROWSER_AUTOPLAY_BLOCK_DETECTED,
  (detected: SetIsBrowserAutoplayBlockDetectedParams) => ({ detected }),
)<SetIsBrowserAutoplayBlockDetectedPayload>();
export type SetIsBrowserAutoplayBlockDetectedAction = ReturnType<
  typeof setIsBrowserAutoplayBlockDetected
>;

type SetIsAntiAdBlockerHammerModeEnabledParams = $TSFixMe;

interface SetIsAntiAdBlockerHammerModeEnabledPayload {
  isEnabled: SetIsAntiAdBlockerHammerModeEnabledParams;
}

export const setIsAntiAdBlockerHammerMode = createAction(
  AppActions.SET_IS_ANTI_AD_BLOCKER_HAMMER_MODE_ENABLED,
  (isEnabled: SetIsAntiAdBlockerHammerModeEnabledParams) => ({ isEnabled }),
)<SetIsAntiAdBlockerHammerModeEnabledPayload>();
export type SetIsAntiAdBlockerHammerModeEnabledAction = ReturnType<
  typeof setIsAntiAdBlockerHammerMode
>;

export const setBrowserAutoplayBlockOpportunityHappened = createAction(
  AppActions.SET_BROWSER_AUTOPLAY_BLOCK_OPPORTUNITY_HAPPENED,
)();
export type SetBrowserAutoplayBlockOpportunityHappenedAction = ReturnType<
  typeof setBrowserAutoplayBlockOpportunityHappened
>;

type DetectAdBlockerParams = $TSFixMe;

interface DetectAdBlockerPayload {
  source: DetectAdBlockerParams;
}

export const detectAdBlocker = createAction(
  AppActions.DETECT_AD_BLOCKER,
  (source: DetectAdBlockerParams) => ({ source }),
)<DetectAdBlockerPayload>();
export type DetectAdBlockerAction = ReturnType<typeof detectAdBlocker>;

type OpenAdBlockerModalParams = $TSFixMe;

interface OpenAdBlockerModalPayload {
  source: OpenAdBlockerModalParams;
}

export const openAdBlockerModal = createAction(
  AppActions.OPEN_AD_BLOCKER_MODAL,
  (source: OpenAdBlockerModalParams) => ({ source }),
)<OpenAdBlockerModalPayload>();
export type OpenAdBlockerModalAction = ReturnType<typeof openAdBlockerModal>;

type OpenLinkedPlaylistUnlockedModalParams = $TSFixMe;

interface OpenLinkedPlaylistUnlockedModalPayload {
  source: OpenLinkedPlaylistUnlockedModalParams;
}

export const openLinkedPlaylistUnlockedModal = createAction(
  AppActions.OPEN_LINKED_PLAYLIST_UNLOCKED_MODAL,
  (source: OpenLinkedPlaylistUnlockedModalParams) => ({ source }),
)<OpenLinkedPlaylistUnlockedModalPayload>();
export type OpenLinkedPlaylistUnlockedModalAction = ReturnType<
  typeof openLinkedPlaylistUnlockedModal
>;

export const closeAdBlockerModal = createAction(AppActions.CLOSE_AD_BLOCKER_MODAL)();
export type CloseAdBlockerModalAction = ReturnType<typeof closeAdBlockerModal>;

export const pageVisibilityChanged = createAction(AppActions.PAGE_VISIBILITY_DID_CHANGE)();
export type PageVisibilityDidChangeAction = ReturnType<typeof pageVisibilityChanged>;

/**
 * Action to signal that the user is entering a playlist page
 */
export const enterPlaylist = createAction(AppActions.ENTER_PLAYLIST)();
export type EnterPlaylistAction = ReturnType<typeof enterPlaylist>;

/**
 * Action to signal that the user is leaving a playlist page
 */
export const leavePlaylist = createAction(AppActions.LEAVE_PLAYLIST)();
export type LeavePlaylistAction = ReturnType<typeof leavePlaylist>;

/**
 * Action to signal that the user is entering a curator backstage page
 */
export const enterCuratorBSP = createAction(AppActions.ENTER_CURATOR_BSP)();
export type EnterCuratorBspAction = ReturnType<typeof enterCuratorBSP>;

/**
 * Action to signal that the user is leaving a curator backstage page
 */
export const leaveCuratorBSP = createAction(AppActions.LEAVE_CURATOR_BSP)();
export type LeaveCuratorBspAction = ReturnType<typeof leaveCuratorBSP>;

/**
 * Action to signal that the user is entering search
 */
export const enterSearch = createAction(AppActions.ENTER_SEARCH)();
export type EnterSearchAction = ReturnType<typeof enterSearch>;

/**
 * Action to signal that the user is leaving search
 */
export const leaveSearch = createAction(AppActions.LEAVE_SEARCH)();
export type LeaveSearchAction = ReturnType<typeof leaveSearch>;

/**
 * Action to force a manual garbage collection on annotations
 */
export const manualGarbageCollection = createAction(AppActions.MANUAL_GARBAGE_COLLECTION)();
export type ManualGarbageCollectionAction = ReturnType<typeof manualGarbageCollection>;

interface ExternalRedirectPayload {
  openWindow: $TSFixMe;
  url: $TSFixMe;
}

export const externalRedirect = createAction(
  AppActions.EXTERNAL_REDIRECT,
  (url: $TSFixMe, openWindow: $TSFixMe = false) => ({ url, openWindow }),
)<ExternalRedirectPayload>();
export type ExternalRedirectAction = ReturnType<typeof externalRedirect>;
